import { useCallback } from 'react'
import { useSearchParams } from "react-router-dom"

type VisibleResult<ValueProp extends string | unknown> = (ValueProp extends string
    ? {
        [name in ValueProp]: string;
    }
    : Record<string, unknown>) & {
    visible: true;
};

type HiddenResult<ValueProp extends string | unknown> = (ValueProp extends string
    ? {
        [name in ValueProp]: undefined;
    }
    : Record<string, unknown>) & {
    visible: false;
};

export type UseLocationModalResult<ValueProp extends string | unknown = unknown> = (ValueProp extends string
    ? {
        show: (value: string) => void;
    }
    : {
        show: () => void;
    }) & {
    hide: (searchParamsArg?: URLSearchParams) => void;
} & (VisibleResult<ValueProp> | HiddenResult<ValueProp>);

/**
 * Hook that allows to store modal visible/hidden state in URL.
 *
 * It returns method that allow to control modal visible/hidden state:
 * - show(param: string) - when user execs this method with some string then `searchParamName` param will
 *                         be appended to URL with given in param string
 * - hide(searchParamsArg?: URLSearchParams) - when user execs this method then
 * `searchParamName` param will be removed from URL, if `searchParamsArg` is passed then it will be used
 *
 * Also, hooks return `visible` variable that control `searchParamName` URL existing and value of
 * `searchParamName`.
 *
 * @example
 * const { visible, caseId, show, hide } = useLocationModal('edit-case', 'caseId');
 * // When user execs `show('123')`, then URL will be like `...?edit-case=123`, `caseId` will contain `123`.
 * // When user execs hide, then `?edit-case` will be removed from URL, caseId will be null
 */
export function useLocationModal<ValueProp extends string | unknown>(
    searchParamName: string,
    valueProp: ValueProp = undefined as ValueProp,
): UseLocationModalResult<ValueProp> {
    const [searchParams, setSearchParams] = useSearchParams()

    const show = useCallback(
        (value: string | undefined) => {
            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.set(searchParamName, value ?? '')
            setSearchParams(newSearchParams)
        },
        [searchParamName, searchParams, setSearchParams],
    )

    const hide = useCallback((searchParamsArg?: URLSearchParams) => {
        const newSearchParams = new URLSearchParams(searchParamsArg || searchParams)
        newSearchParams.delete(searchParamName)
        setSearchParams(newSearchParams)
    }, [searchParamName, searchParams, setSearchParams])

    const valuePart = (() => {
        if (!valueProp) {
            return {
                visible: searchParams.has(searchParamName)
            }
        }

        return {
            visible: searchParams.has(searchParamName),
            [valueProp as string]: searchParams.get(searchParamName),
        }
    })()

    return {
        show,
        hide,
        ...valuePart,
    } as UseLocationModalResult<ValueProp>
}
